import { z } from "zod";

export const BRIDGE_PROTOCOL_VERSION = 1;

/** The bridge release this Quovy ships; a connected bridge on an older one is told to update. */
export const BRIDGE_LATEST_VERSION = "0.10.0";

/** True when `version` is an older bridge release than `than`; a pre-release sorts before its release. */
export function isOlderBridgeVersion(
  version: string,
  than: string = BRIDGE_LATEST_VERSION,
): boolean {
  const parse = (value: string) => {
    const [core = "", pre] = value.split("-", 2);
    return {
      parts: core.split(".").map((part) => Number.parseInt(part, 10) || 0),
      pre,
    };
  };
  const a = parse(version);
  const b = parse(than);
  for (let i = 0; i < 3; i++) {
    const diff = (a.parts[i] ?? 0) - (b.parts[i] ?? 0);
    if (diff !== 0) return diff < 0;
  }
  return a.pre !== undefined && b.pre === undefined;
}

export const BRIDGE_CAPABILITY = {
  http: "http",
  fetch: "fetch",
  cancel: "cancel",
  upload: "upload",
  tunnel: "tunnel",
  secret: "secret",
  smb: "smb",
} as const;
const LEGACY_MYSQL_CAPABILITY = "mysql";

export const zBridgeDatabaseEngine = z.string().regex(/^[a-z][a-z0-9]{0,31}$/);

export function bridgeDatabaseCapability(engine: string): string {
  return `db:${engine}`;
}

export function bridgeEngineCapabilities(engine: string): string[] {
  return engine === "mysql"
    ? [bridgeDatabaseCapability(engine), LEGACY_MYSQL_CAPABILITY]
    : [bridgeDatabaseCapability(engine)];
}

export function bridgeEngines(capabilities: readonly string[]): string[] {
  const engines = new Set<string>();
  for (const capability of capabilities) {
    if (capability === LEGACY_MYSQL_CAPABILITY) engines.add("mysql");
    else if (capability.startsWith("db:")) engines.add(capability.slice(3));
  }
  return [...engines];
}

export const BRIDGE_KEY_PREFIX = "qbr_";

export const BRIDGE_CONNECT_PATH = "/v1/bridge/connect";
export const BRIDGE_UPLOAD_PATH_PREFIX = "/v1/bridge/uploads/";
/** Where a bridge dials back to carry one tunnelled connection. */
export const BRIDGE_TUNNEL_PATH_PREFIX = "/v1/bridge/tunnels/";
/** Where a sandbox opens one remote desktop connection, with its grant token as the bearer. */
export const REMOTE_DESKTOP_TUNNEL_PATH = "/v1/remote-desktop/tunnel";
const BRIDGE_TUNNEL_DEFAULT_PORT = 3389;
const TUNNEL_HOST = /^[a-z0-9]([a-z0-9.-]{0,251}[a-z0-9])?$/;

export type BridgeTunnelAddress = {
  host: string;
  port: number;
  address: string;
};

/** Parses `rdp://host[:port]` into the canonical form allowlists and grants compare. */
export function bridgeTunnelAddress(raw: string): BridgeTunnelAddress | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  if (
    url.protocol !== "rdp:" ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    (url.pathname !== "" && url.pathname !== "/") ||
    !TUNNEL_HOST.test(host)
  ) {
    return null;
  }
  const port = url.port ? Number(url.port) : BRIDGE_TUNNEL_DEFAULT_PORT;
  if (port < 1) return null;
  return { host, port, address: `rdp://${host}:${port}` };
}

// Key Vault's own naming rules, so a malformed name is refused before any request leaves the bridge.
const KEY_VAULT_NAME = /^[a-z][a-z0-9-]{1,22}[a-z0-9]$/;
const KEY_VAULT_SECRET_NAME = /^[0-9a-z-]{1,127}$/;

export type BridgeSecretAddress = {
  vault: string;
  secret: string;
  address: string;
};

/** Canonical `kv://vault/secret`; Key Vault names are case-insensitive, so both are lowercased. */
export function bridgeSecretAddress(vault: string, secret: string): BridgeSecretAddress | null {
  const v = vault.trim().toLowerCase();
  const s = secret.trim().toLowerCase();
  if (!KEY_VAULT_NAME.test(v) || v.includes("--") || !KEY_VAULT_SECRET_NAME.test(s)) return null;
  return { vault: v, secret: s, address: `kv://${v}/${s}` };
}

/** Parses an allowlist entry `kv://vault/secret`; no wildcard, version or query. */
export function parseBridgeSecretAddress(raw: string): BridgeSecretAddress | null {
  const match = /^kv:\/\/([^/?#]+)\/([^/?#]+)$/i.exec(raw.trim());
  return match ? bridgeSecretAddress(match[1]!, match[2]!) : null;
}

const SMB_DEFAULT_PORT = 445;
const SMB_SERVER = /^[a-z0-9]([a-z0-9.-]{0,251}[a-z0-9])?$/;
// Windows refuses these in a file or folder name; a control character can split a log line.
const SMB_BAD_NAME = /[\\/:*?"<>|\x00-\x1f]/;

/** A path on a Windows file share, compared case-insensitively as Windows does. */
export type BridgeSmbAddress = {
  server: string;
  port: number;
  share: string;
  /** Folders and file under the share, decoded, in their original case. */
  segments: string[];
  /** Canonical `smb://server:port/share/...`, lowercased, segments percent-encoded. */
  address: string;
};

/**
 * Parses `smb://server[:port]/share[/folder...]`. Segments may be percent-encoded; an empty
 * segment, `.` or `..`, or one that decodes to a separator is refused rather than normalized,
 * so a path can never climb out of the folder an allowlist names. A trailing `/` is allowed.
 */
export function bridgeSmbAddress(raw: string): BridgeSmbAddress | null {
  const match = /^smb:\/\/([^/:?#@\\]+)(?::(\d{1,5}))?\/([^?#]*)$/i.exec(raw.trim());
  if (!match) return null;
  const server = match[1]!.toLowerCase();
  const port = match[2] ? Number(match[2]) : SMB_DEFAULT_PORT;
  if (!SMB_SERVER.test(server) || port < 1 || port > 65535) return null;
  const parts = match[3]!.replace(/\/$/, "").split("/");
  const decoded: string[] = [];
  for (const part of parts) {
    let name: string;
    try {
      name = decodeURIComponent(part);
    } catch {
      return null;
    }
    if (!name || name === "." || name === ".." || SMB_BAD_NAME.test(name)) return null;
    decoded.push(name);
  }
  const [share, ...segments] = decoded;
  if (!share) return null;
  const address = `smb://${server}:${port}/${decoded.map((name) => encodeURIComponent(name.toLowerCase())).join("/")}`;
  return { server, port, share, segments, address };
}

/** True when `target` is the folder an allowlist entry names or inside it, on whole segments. */
export function bridgeSmbCovers(entry: BridgeSmbAddress, target: BridgeSmbAddress): boolean {
  return entry.address === target.address || target.address.startsWith(`${entry.address}/`);
}

/** The allowlist entry that covers `target`, or null; entries that are not `smb://` are skipped. */
export function bridgeSmbAllowedBy(
  allowed: Iterable<string>,
  target: BridgeSmbAddress,
): BridgeSmbAddress | null {
  for (const raw of allowed) {
    const entry = raw.toLowerCase().startsWith("smb:") ? bridgeSmbAddress(raw) : null;
    if (entry && bridgeSmbCovers(entry, target)) return entry;
  }
  return null;
}

export function isBridgeSmbUrl(url: string): boolean {
  return url.slice(0, 4).toLowerCase() === "smb:";
}

/** The JSON a bridge answers a `GET smb://…/folder/` with. */
export const zBridgeSmbListing = z.object({
  entries: z.array(
    z.object({
      name: z.string().min(1),
      kind: z.enum(["file", "folder"]),
      bytes: z.number().int().min(0).optional(),
      modified: z.string().optional(),
    }),
  ),
  truncated: z.boolean(),
});
export type BridgeSmbListing = z.infer<typeof zBridgeSmbListing>;

export const BRIDGE_CLOSE = {
  protocol: 4400,
  unauthorized: 4401,
  featureOff: 4403,
  replaced: 4409,
  revoked: 4410,
  /** The key is valid but belongs to another workspace than the bridge hostname names. */
  wrongAddress: 4404,
  /** The address sent too many bad keys; the bridge should wait and retry, not exit. */
  tooManyAttempts: 4429,
} as const;

export const BRIDGE_MAX_FRAME_BYTES = 4 * 1024 * 1024;
/** A call's error when its bridge dropped before answering: the request may or may not have run. */
export const BRIDGE_DISCONNECTED_MESSAGE =
  "The bridge disconnected before it answered. Check the bridge's logs, then try again.";
/** Longest message an error frame may carry; the API closes a socket that sends a longer one. */
export const BRIDGE_ERROR_MESSAGE_MAX = 500;
/** A bare identifier, so a code can never carry a host or a path. */
export const BRIDGE_ERROR_CODE = /^[A-Z0-9_]{1,64}$/;

const zHeaders = z.record(z.string(), z.string());

export const zBridgeHello = z.object({
  type: z.literal("hello"),
  protocol: z.number().int(),
  version: z.string().min(1).max(64),
  capabilities: z.array(z.string().min(1).max(40)).max(64).optional(),
  allowed: z.array(z.string().min(1).max(300)).max(100).optional(),
});
export type BridgeHello = z.infer<typeof zBridgeHello>;

export const zBridgeReady = z.object({
  type: z.literal("ready"),
  // Deployed bridges read the bridge's name under `integrationName`.
  integrationName: z.string(),
  // What the Datadog agent beside the bridge reports with. Loose on purpose: a
  // value the bridge refuses must not cost it the whole ready frame.
  datadog: z
    .object({
      apiKey: z.string().min(1).max(200),
      site: z.string().min(1).max(100),
    })
    .optional()
    .catch(undefined),
});
export type BridgeReady = z.infer<typeof zBridgeReady>;

export const zBridgeHttpRequest = z.object({
  type: z.literal("http_request"),
  id: z.string().min(1),
  method: z.enum(["POST", "DELETE"]),
  url: z.string().min(1),
  headers: zHeaders,
  body: z.string().nullable(),
});
export type BridgeHttpRequest = z.infer<typeof zBridgeHttpRequest>;

export const zBridgeHttpResponse = z.object({
  type: z.literal("http_response"),
  id: z.string().min(1),
  status: z.number().int().min(100).max(599),
  headers: zHeaders,
  body: z.string(),
});
export type BridgeHttpResponse = z.infer<typeof zBridgeHttpResponse>;

export const zBridgeHttpError = z.object({
  type: z.literal("http_error"),
  id: z.string().min(1),
  message: z.string().max(BRIDGE_ERROR_MESSAGE_MAX),
});
export type BridgeHttpError = z.infer<typeof zBridgeHttpError>;

export const BRIDGE_FETCH_MAX_BYTES = 256 * 1024 * 1024;
export const BRIDGE_FETCH_MAX_TIMEOUT_MS = 120_000;
export const BRIDGE_FETCH_CHUNK_BYTES = 1024 * 1024;

export const zBridgeFetchRequest = z.object({
  type: z.literal("fetch_request"),
  id: z.string().min(1),
  method: z.enum(["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"]),
  url: z.string().min(1).max(8000),
  headers: zHeaders,
  body: z.string().nullable(),
  auth: z.enum(["none", "azure_identity"]),
  responseHeaders: z.array(z.string().min(1).max(64)).max(32),
  maxResponseBytes: z.number().int().min(1).max(BRIDGE_FETCH_MAX_BYTES),
  timeoutMs: z.number().int().min(1_000).max(BRIDGE_FETCH_MAX_TIMEOUT_MS),
  uploadPath: z.string().startsWith(BRIDGE_UPLOAD_PATH_PREFIX).max(200).optional(),
});
export type BridgeFetchRequest = z.infer<typeof zBridgeFetchRequest>;

export const zBridgeFetchHead = z.object({
  type: z.literal("fetch_head"),
  id: z.string().min(1),
  status: z.number().int().min(100).max(599),
  headers: zHeaders,
});
export type BridgeFetchHead = z.infer<typeof zBridgeFetchHead>;

export const zBridgeFetchChunk = z.object({
  type: z.literal("fetch_chunk"),
  id: z.string().min(1),
  seq: z.number().int().min(0),
  data: z.string(),
});
export type BridgeFetchChunk = z.infer<typeof zBridgeFetchChunk>;

export const zBridgeFetchEnd = z.object({
  type: z.literal("fetch_end"),
  id: z.string().min(1),
});
export type BridgeFetchEnd = z.infer<typeof zBridgeFetchEnd>;

export const zBridgeFetchError = z.object({
  type: z.literal("fetch_error"),
  id: z.string().min(1),
  message: z.string().max(BRIDGE_ERROR_MESSAGE_MAX),
  /** Node's error code for a failed connection (`UNABLE_TO_VERIFY_LEAF_SIGNATURE`, `ECONNREFUSED`, ...), from bridge 0.9.1. */
  code: z.string().regex(BRIDGE_ERROR_CODE).optional(),
});
export type BridgeFetchError = z.infer<typeof zBridgeFetchError>;

export const zBridgeTunnelRequest = z.object({
  type: z.literal("tunnel_request"),
  id: z.string().min(1),
  /** `rdp://host:port`, matched against the bridge's allowlist. */
  target: z.string().min(1).max(300),
  tunnelPath: z.string().startsWith(BRIDGE_TUNNEL_PATH_PREFIX).max(200),
});
export type BridgeTunnelRequest = z.infer<typeof zBridgeTunnelRequest>;

export const zBridgeTunnelOpen = z.object({
  type: z.literal("tunnel_open"),
  id: z.string().min(1),
});
export type BridgeTunnelOpen = z.infer<typeof zBridgeTunnelOpen>;

export const zBridgeTunnelError = z.object({
  type: z.literal("tunnel_error"),
  id: z.string().min(1),
  message: z.string().max(BRIDGE_ERROR_MESSAGE_MAX),
});
export type BridgeTunnelError = z.infer<typeof zBridgeTunnelError>;

export const BRIDGE_SECRET_READ_REASONS = [
  "not_allowed",
  "not_found",
  "forbidden",
  "access_policy",
  "disabled",
  "unreachable",
  "identity_unavailable",
  "invalid_value",
  "failed",
] as const;
export type BridgeSecretReadReason = (typeof BRIDGE_SECRET_READ_REASONS)[number];

/** A secret readable only by the caller holding the matching private key (`@quovy/types/bridge-seal`). */
export const zBridgeSealed = z.object({
  ephemeralKey: z.string().min(1).max(100),
  iv: z.string().min(1).max(40),
  ciphertext: z.string().min(1).max(4000),
  tag: z.string().min(1).max(40),
});
export type BridgeSealed = z.infer<typeof zBridgeSealed>;

export const zBridgeSecretReadRequest = z.object({
  type: z.literal("secret_read_request"),
  id: z.string().min(1),
  vault: z.string().min(1).max(24),
  secret: z.string().min(1).max(127),
  /** `check` proves the secret can be read and returns its version, never its value. */
  mode: z.enum(["read", "check"]),
  /** Raw X25519 public key, base64; the value is sealed to it. Required to read. */
  replyKey: z.string().min(1).max(100).optional(),
});
export type BridgeSecretReadRequest = z.infer<typeof zBridgeSecretReadRequest>;

export const zBridgeSecretReadResponse = z.object({
  type: z.literal("secret_read_response"),
  id: z.string().min(1),
  version: z.string().min(1).max(64),
  /** Absent for a check. Opened with `createSealKey` from `@quovy/types/bridge-seal`. */
  sealed: zBridgeSealed.optional(),
});
export type BridgeSecretReadResponse = z.infer<typeof zBridgeSecretReadResponse>;

export const zBridgeSecretReadError = z.object({
  type: z.literal("secret_read_error"),
  id: z.string().min(1),
  reason: z.enum(BRIDGE_SECRET_READ_REASONS).optional(),
  message: z.string().max(BRIDGE_ERROR_MESSAGE_MAX),
});
export type BridgeSecretReadError = z.infer<typeof zBridgeSecretReadError>;

const zSqlValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type BridgeSqlValue = z.infer<typeof zSqlValue>;

export const zBridgeDatabaseTarget = z.object({
  engine: zBridgeDatabaseEngine,
  host: z.string().min(1).max(253),
  port: z.number().int().min(1).max(65535),
  database: z.string().min(1).max(64),
  auth: z.enum(["azure_identity", "password"]),
  user: z.string().min(1).max(128).optional(),
});
export type BridgeDatabaseTarget = z.infer<typeof zBridgeDatabaseTarget>;

export const BRIDGE_QUERY_MAX_ROWS = 1000;

export const zBridgeQueryRequest = z.object({
  type: z.literal("query_request"),
  id: z.string().min(1),
  target: zBridgeDatabaseTarget,
  sql: z.string().min(1).max(20_000),
  params: z.array(zSqlValue).max(100),
  maxRows: z.number().int().min(1).max(BRIDGE_QUERY_MAX_ROWS),
  timeoutMs: z.number().int().min(1_000).max(55_000),
});
export type BridgeQueryRequest = z.infer<typeof zBridgeQueryRequest>;

export const zBridgeQueryResponse = z.object({
  type: z.literal("query_response"),
  id: z.string().min(1),
  columns: z.array(z.string()),
  rows: z.array(z.array(zSqlValue)),
  truncated: z.boolean(),
});
export type BridgeQueryResponse = z.infer<typeof zBridgeQueryResponse>;

export const zBridgeQueryError = z.object({
  type: z.literal("query_error"),
  id: z.string().min(1),
  message: z.string().max(BRIDGE_ERROR_MESSAGE_MAX),
});
export type BridgeQueryError = z.infer<typeof zBridgeQueryError>;

export const zBridgeCallRequest = z.discriminatedUnion("type", [
  zBridgeHttpRequest,
  zBridgeQueryRequest,
  zBridgeFetchRequest,
  zBridgeTunnelRequest,
  zBridgeSecretReadRequest,
]);
export type BridgeCallRequest = z.infer<typeof zBridgeCallRequest>;

export function bridgeRequestCapabilities(request: BridgeCallRequest): string[] {
  if (request.type === "http_request") return [BRIDGE_CAPABILITY.http];
  if (request.type === "fetch_request") {
    return isBridgeSmbUrl(request.url) ? [BRIDGE_CAPABILITY.smb] : [BRIDGE_CAPABILITY.fetch];
  }
  if (request.type === "tunnel_request") return [BRIDGE_CAPABILITY.tunnel];
  if (request.type === "secret_read_request") return [BRIDGE_CAPABILITY.secret];
  return bridgeEngineCapabilities(request.target.engine);
}

export const zBridgeCancel = z.object({
  type: z.literal("cancel"),
  id: z.string().min(1),
});
export type BridgeCancel = z.infer<typeof zBridgeCancel>;

export const zBridgeDraining = z.object({
  type: z.literal("draining"),
});
export type BridgeDraining = z.infer<typeof zBridgeDraining>;

export const zBridgeToApiFrame = z.discriminatedUnion("type", [
  zBridgeHello,
  zBridgeDraining,
  zBridgeHttpResponse,
  zBridgeHttpError,
  zBridgeQueryResponse,
  zBridgeQueryError,
  zBridgeFetchHead,
  zBridgeFetchChunk,
  zBridgeFetchEnd,
  zBridgeFetchError,
  zBridgeTunnelOpen,
  zBridgeTunnelError,
  zBridgeSecretReadResponse,
  zBridgeSecretReadError,
]);
export type BridgeToApiFrame = z.infer<typeof zBridgeToApiFrame>;

export const zApiToBridgeFrame = z.discriminatedUnion("type", [
  zBridgeReady,
  zBridgeHttpRequest,
  zBridgeQueryRequest,
  zBridgeFetchRequest,
  zBridgeTunnelRequest,
  zBridgeSecretReadRequest,
  zBridgeCancel,
]);
export type ApiToBridgeFrame = z.infer<typeof zApiToBridgeFrame>;

export function bridgeCallFailure(
  request: Pick<BridgeCallRequest, "type" | "id">,
  message: string,
):
  | BridgeHttpError
  | BridgeQueryError
  | BridgeFetchError
  | BridgeTunnelError
  | BridgeSecretReadError {
  const type =
    request.type === "query_request"
      ? "query_error"
      : request.type === "fetch_request"
        ? "fetch_error"
        : request.type === "tunnel_request"
          ? "tunnel_error"
          : request.type === "secret_read_request"
            ? "secret_read_error"
            : "http_error";
  return { type, id: request.id, message };
}
